'use client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { BaseFillIntelligentDuration } from '@/app/components/icons/BaseFillIntelligentDuration';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { StarIcon } from '@/app/components/icons/StarIcon';
import { ViduLogo2 } from '@/app/components/icons/ViduLogo2';
import { CouponBadge } from '@/app/components/shared/CouponBadge';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import {
  CURRENCY,
  NEW_MEMBERSHIP_NOPEAK_LIMITS,
  NEW_MEMBERSHIP_REFERENCE_LIMIT,
  REFERENCE_RIGHT_LIST,
  SITE,
} from '@/app/constants';
import { useMembershipRevision, useSyncHeight } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { PaidTrialInfo, SubscriptionProductType } from '@/app/types';
import { cn, formatAmount, formatPrice, formatPriceDiscount, hasSelectedCoupon } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import {
  CREDITS_CONFIG_LIST,
  type IndividualPlanType,
  NEW_MEMBERSHIP_CREDITS_CONFIG_LIST,
  NEW_MEMBERSHIP_HIGHLIGHTED_RIGHTS,
  PLAN_COMMON_RIGHTS,
  PLAN_HIGHLIGHTED_RIGHTS,
  PLAN_TITLE_TYPE_MAP,
  Q4_CONSUMPTION_CONFIG,
  Q4_CONSUMPTION_RESOLUTIONS,
  TRIAL_CREDITS_CONFIG,
} from '../constants';
import type { PlanRight } from '../types';
import { getCreditsDiscount, getCreditsProgress } from '../utils';
import { CreditsProgressBar } from './CreditsProgressBar';

// StarIcon 的渐变是写死的（#96C5FF→白），无法按档位改色。
// 这里用星形轮廓作 CSS mask，让星星显示每档积分同款渐变（bgImg 背景透过 mask 呈现）。
const STAR_MASK =
  'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAyNCAyNCc+PHBhdGggZD0nTTEwLjYyOTQgMC4yOTEyODhDMTAuNzMxOCAtMC4wMjk4MTE4IDExLjI2ODIgLTAuMDI5ODExMyAxMS4zNzA2IDAuMjkxMjg5QzExLjkyMzEgMi4wMjQ5NSAxMy4xMDU2IDUuMTA1NjQgMTUgN0MxNi44NjA0IDguODYwNDEgMTkuODY1IDEwLjAzNDMgMjEuNjE0MiAxMC41OTkxQzIxLjk0MDEgMTAuNzA0NCAyMS45MzM3IDExLjI3MDggMjEuNjA2MiAxMS4zNzFDMTkuODk4MiAxMS44OTM4IDE2Ljk4NzEgMTMuMDEyOSAxNSAxNUMxMi45ODc2IDE3LjAxMjQgMTEuODY1NSAxOS45NzI1IDExLjM1MTQgMjEuNjcwOEMxMS4yNTMyIDIxLjk5NTEgMTAuNzA2OSAyMi4wMDQ3IDEwLjYgMjEuNjgzMUMxMC4wMTI1IDE5LjkxNDcgOC43ODg2MiAxNi43ODg2IDcgMTVDNS4xODA4NSAxMy4xODA5IDEuOTc4MjMgMTEuOTQ1OCAwLjIyNzU5OCAxMS4zNzA0Qy0wLjA4OTE2NTMgMTEuMjY2MyAtMC4wODQ1OTYxIDEwLjc0ODMgMC4yMzMzMzMgMTAuNjQ3OEMxLjk1NTUzIDEwLjEwMzQgNS4wODQzNCA4LjkxNTY2IDcgN0M4Ljg5NDM3IDUuMTA1NjMgMTAuMDc2OSAyLjAyNDk1IDEwLjYyOTQgMC4yOTEyODhaJyBmaWxsPSdibGFjaycvPjwvc3ZnPg==")';

const GIFT_MASK =
  'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAyNCAyNCc+PHBhdGggZD0nTTIgNy43NUMyIDcuMTk3NzIgMi40NDc3MiA2Ljc1IDMgNi43NUgyMUMyMS41NTIzIDYuNzUgMjIgNy4xOTc3MiAyMiA3Ljc1VjkuMjVDMjIgOS44MDIyOCAyMS41NTIzIDEwLjI1IDIxIDEwLjI1SDNDMi40NDc3MiAxMC4yNSAyIDkuODAyMjggMiA5LjI1VjcuNzVaJyBmaWxsPSdibGFjaycvPjxwYXRoIGQ9J002IDVDNiAzLjYxOTI5IDcuMTE5MjkgMi41IDguNSAyLjVIOUMxMC42NTY5IDIuNSAxMiAzLjg0MzE1IDEyIDUuNVY3LjVIOC41QzcuMTE5MjkgNy41IDYgNi4zODA3MSA2IDVWNVonIHN0cm9rZT0nYmxhY2snIHN0cm9rZS13aWR0aD0nMS41Jy8+PHBhdGggZD0nTTE4IDVDMTggMy42MTkyOSAxNi44ODA3IDIuNSAxNS41IDIuNUgxNUMxMy4zNDMxIDIuNSAxMiAzLjg0MzE1IDEyIDUuNVY3LjVIMTUuNUMxNi44ODA3IDcuNSAxOCA2LjM4MDcxIDE4IDVWNVonIHN0cm9rZT0nYmxhY2snIHN0cm9rZS13aWR0aD0nMS41Jy8+PHBhdGggZD0nTTEyLjc1IDExLjc1SDIxVjE5Ljc1QzIxIDIwLjg1NDYgMjAuMTA0NiAyMS43NSAxOSAyMS43NUgxMi43NVYxMS43NVonIGZpbGw9J2JsYWNrJy8+PHBhdGggZD0nTTMgMTEuNzVIMTEuMjVWMjEuNzVINUMzLjg5NTQzIDIxLjc1IDMgMjAuODU0NiAzIDE5Ljc1VjExLjc1WicgZmlsbD0nYmxhY2snLz48L3N2Zz4=")';

const SPARKLE_MASK =
  'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAyNCAyNCc+PHBhdGggZD0nTTExLjI3NTkgNS4wNjg5N0MxMS4zOTU1IDQuMjMxNTggMTIuNjA0NSA0LjIzMTU4IDEyLjcyNDEgNS4wNjg5N0MxMy4xODM5IDguMjg3MzYgMTUuNzEyNiAxMC44MTYxIDE4LjkzMSAxMS4yNzU5QzE5Ljc2ODQgMTEuMzk1NSAxOS43Njg0IDEyLjYwNDUgMTguOTMxIDEyLjcyNDFDMTUuNzEyNiAxMy4xODM5IDEzLjE4MzkgMTUuNzEyNiAxMi43MjQxIDE4LjkzMUMxMi42MDQ1IDE5Ljc2ODQgMTEuMzk1NSAxOS43Njg0IDExLjI3NTkgMTguOTMxQzEwLjgxNjEgMTUuNzEyNiA4LjI4NzM2IDEzLjE4MzkgNS4wNjg5NyAxMi43MjQxQzQuMjMxNTggMTIuNjA0NSA0LjIzMTU4IDExLjM5NTUgNS4wNjg5NyAxMS4yNzU5QzguMjg3MzYgMTAuODE2MSAxMC44MTYxIDguMjg3MzYgMTEuMjc1OSA1LjA2ODk3WicgZmlsbD0nYmxhY2snLz48L3N2Zz4=")';

const ULTIMATE_TITLE_GRADIENT = 'linear-gradient(90deg, #E0DDFF 2.78%, #B494F2 95.83%)';

// 专享「快速 / 高速 / 极速生成通道」权益，icon + 文字走各档位渐变
const SPEED_CHANNEL_RIGHTS = new Set<PlanRight>(['fastSpeedRight', 'highSpeedRight', 'ultraFastSpeedRight']);

export const PlanCard = ({
  product,
  renderSubscriptionButton,
  isDiscount,
  showPrice,
  isPaidTrial = false,
  paidTrialInfo,
  productSelector,
  credits,
}: {
  product: SubscriptionProductType;
  renderSubscriptionButton?: (product: SubscriptionProductType) => React.ReactNode;
  isDiscount?: boolean;
  hideExpiryWarning?: boolean;
  showPrice?: boolean;
  /** True when the card is displayed under the paid-trial tab. */
  isPaidTrial?: boolean;
  /** Trial pricing info, present only when isPaidTrial is true. */
  paidTrialInfo?: PaidTrialInfo;
  productSelector?: ReactNode;
  /** 仅多商品旗舰版展示所选商品的月度积分，其余仍使用原有配置。 */
  credits?: number;
}) => {
  const bonusCode = useStore((state) => state.bonusCode);

  const t = useTranslations();
  const locale = useLocale();
  const isNewMembership = useMembershipRevision();
  useSyncHeight({ selector: '.plan-title-section' });
  useSyncHeight({ selector: '.price-section' });

  const { isEligible: isRetentionEligible } = useRetentionStrategy();

  if (!product) return null;
  // team_v2 走独立的 TeamPlanCard，本组件断言 plan_type 不会是 team_v2。
  const planType = product.plan_type as IndividualPlanType;
  const { extra_grant_credit = 0 } = product;
  const monthsInPeriod = product.billing_period === 'yearly' ? 12 : 1;
  const hasCoupon = hasSelectedCoupon(product);

  // 卡面划线价：优先用按「按月购买原价」派生的展示基准，缺省回落到商品自身原价。
  // 与下方「下个月续费金额」读取的 original_price 是两条独立来源，互不牵连。
  const strikethroughPrice = product.strikethrough_price ?? product.original_price ?? 0;

  // 优先使用产品自身的 paid_trial，fallback 到外部传入的 paidTrialInfo
  const effectiveTrialInfo = product.paid_trial
    ? {
        price: product.paid_trial.price ?? 0,
        duration_days: product.paid_trial.duration_days ?? 0,
      }
    : paidTrialInfo;

  const showRetentionDiscount =
    isRetentionEligible && product.product_type === 'subscription' && product.billing_period === 'monthly';

  // 试用商品（如 7 天试用）在套餐名旁展示试用标签，免费版除外
  const showTrialTag = isPaidTrial && planType !== 'free' && Boolean(effectiveTrialInfo);

  const renderTrialTag = (): ReactNode => (
    <div
      className="rounded-8 px-2 py-1 font-medium text-sm text-system-white uppercase italic"
      style={{ backgroundImage: 'linear-gradient(90deg, #767BFF 0%, #F08DFF 100%)' }}
    >
      {t('payments.plans.newUserTrialBadge')}
    </div>
  );

  const renderDiscountBadge = (): ReactNode => {
    if (product.discount > 0) {
      if (hasCoupon) {
        return <CouponBadge discount={product.discount} />;
      }

      if (showRetentionDiscount) {
        return (
          <div className="rounded-8 border border-white/30 bg-gradient-to-r from-[#b2d7ff] to-[#ffe3da] px-2 py-1 font-bold text-system-black text-xs uppercase italic shadow-[inset_0px_0px_6px_0px_white]">
            限时{formatPriceDiscount(product.discount, 'zh')}折
          </div>
        );
      }

      return (
        <div className="rounded-8 bg-[linear-gradient(90deg,#FF7678_0%,#FF383B_100%)] px-2 py-1 font-bold text-white text-xs uppercase italic leading-[1.4]">
          {t('payments.plans.discount', {
            discount: formatPriceDiscount(product.discount, locale),
          })}
        </div>
      );
    }

    return null;
  };

  const creditsDiscount = getCreditsDiscount(planType, locale);

  // 试用 tab 下使用试用积分/视频，否则使用标准月度配置
  const useTrialCredits = isPaidTrial && planType !== 'free' && planType in TRIAL_CREDITS_CONFIG;
  const creditsConfig = (isNewMembership ? NEW_MEMBERSHIP_CREDITS_CONFIG_LIST : CREDITS_CONFIG_LIST)[planType];

  // 月度积分的数值形式：displayCredits 是 toLocaleString 出来的字符串（如 "10,000"），
  // 无法参与价格计算；creditsConfig.credits 的类型是 number | string，一并收成数字。
  const monthlyCredits = useTrialCredits
    ? TRIAL_CREDITS_CONFIG[planType as keyof typeof TRIAL_CREDITS_CONFIG].credits
    : Number(credits ?? creditsConfig.credits);

  const displayCredits = useTrialCredits
    ? TRIAL_CREDITS_CONFIG[planType as keyof typeof TRIAL_CREDITS_CONFIG].credits
    : credits === undefined
      ? creditsConfig.credits
      : credits.toLocaleString(locale);

  // 每 100 积分的价格（分）。分子取「月均实付」而非周期总价，
  // 与卡面大号价格的口径保持一致；年付直接用总价会虚高 12 倍。
  // 试用档不展示：试用价与试用积分是两个独立配置，相除无业务含义。
  const monthlyPrice = product.price / monthsInPeriod;
  const creditsUnitPriceCents = monthlyCredits > 0 ? (monthlyPrice / monthlyCredits) * 100 : 0;
  // 580a 新增的特殊权益统一随会员活动开关生效；白名单用户仍可提前预览。
  const showCreditsUnitPrice =
    isNewMembership === true && !useTrialCredits && monthlyCredits > 0 && creditsUnitPriceCents > 0;
  const highlightedRights = (isNewMembership ? NEW_MEMBERSHIP_HIGHLIGHTED_RIGHTS : PLAN_HIGHLIGHTED_RIGHTS)[planType];
  const commonRights = PLAN_COMMON_RIGHTS[planType];
  const referenceNumber =
    isNewMembership && planType !== 'free' ? NEW_MEMBERSHIP_REFERENCE_LIMIT : REFERENCE_RIGHT_LIST[planType];

  const isUltimate = planType === 'ultimate_v2';

  // 专享错峰通道：新会员按档位放量（count>0 才展示），旧版仅旗舰版展示固定文案
  const showLegacyNopeakChannel = isNewMembership !== true && isUltimate;
  const showNewNopeakChannel =
    isNewMembership === true && planType !== 'free' && NEW_MEMBERSHIP_NOPEAK_LIMITS[planType] > 0;
  // Q4 模型消耗是 580a 新增的活动权益，严格跟随会员活动开关。
  const showQ4Consumption = isNewMembership === true && !!Q4_CONSUMPTION_CONFIG[planType];
  // 三条特殊权益（100积分单价 / 专享通道 / Q4 消耗）合并成的独立模块是否展示
  const showBenefitHighlights = showCreditsUnitPrice || showNewNopeakChannel || showQ4Consumption;

  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-center justify-start overflow-hidden',
        isUltimate && 'rounded-16 bg-[#5765F2]',
      )}
    >
      <div className="flex h-11 shrink-0 items-center justify-center">
        {isUltimate && (
          <div className="flex items-center gap-1 font-medium text-sm text-white leading-5">
            <StarIcon className="size-[14px] shrink-0" />
            <span>{t('payments.plans.bestValue')}</span>
          </div>
        )}
      </div>

      <div
        className={cn(
          'relative flex h-full w-full flex-col justify-start overflow-hidden rounded-16 border-2 border-system-white16 px-4 py-6 text-system-white',
          planType === 'free' && 'bg-[#0A0D10]',
          planType === 'standard_v2' && 'bg-[#0E1115] shadow-[inset_0_0_100px_0_rgba(207,231,255,0.08)]',
          planType === 'premium_v2' &&
            'border-[rgba(250,241,200,0.16)] bg-[#12100D] shadow-[inset_0_0_100px_0_rgba(250,241,200,0.08)]',
          isUltimate && 'border-[#5765F2] bg-[linear-gradient(180deg,#161722_0%,rgba(30,30,30,0.64)_100%)]',
        )}
      >
        {/* 套餐名称 */}
        <div className="plan-title-section border-system-white12 border-b pb-4 font-semibold text-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="flex flex-wrap items-center gap-2 bg-clip-text text-system-white text-transparent"
              style={PLAN_TITLE_TYPE_MAP[planType]}
            >
              {t(`payments.${planType}`)}
            </div>
            {showTrialTag ? renderTrialTag() : isDiscount && renderDiscountBadge()}
          </div>

          {/* 套餐说明 */}
          {planType !== 'free' && (
            <div className="mt-1 font-normal text-system-white40 text-xs leading-[1.4]">
              {t(`payments.plans.planDescription.${planType}`)}
            </div>
          )}
        </div>

        {/* 价格 */}
        {(renderSubscriptionButton || showPrice) && (
          <div className="price-section py-6 text-sm text-system-white">
            {isPaidTrial && effectiveTrialInfo && planType !== 'free' ? (
              <>
                {/* Trial pricing */}
                <div className="flex items-end gap-1 leading-none">
                  <span className="font-semibold text-2xl text-system-white40 leading-[1.4]">{CURRENCY}</span>
                  <span className="font-semibold text-[48px] leading-none">
                    {formatAmount(effectiveTrialInfo.price)}
                  </span>
                  <span className="text-base text-system-white16 leading-[1.4]">/</span>
                  <span className="text-base text-system-white40 leading-[1.4]">
                    {t('payments.plans.trialDuration', { days: effectiveTrialInfo.duration_days })}
                  </span>
                </div>
                <div className="mt-2 text-sm text-system-white40">
                  {t('payments.plans.trialRenewal', {
                    days: effectiveTrialInfo.duration_days,
                    firstMonthPrice: formatPrice(product.price),
                    monthlyPrice: formatPrice(product.original_price ?? product.price),
                  })}
                </div>
              </>
            ) : (
              <>
                {/* Standard pricing */}
                <div className="flex items-end gap-1 leading-none">
                  <span className="font-semibold text-2xl text-system-white40 leading-[1.4]">{CURRENCY}</span>
                  <span className="font-semibold text-[48px] leading-none">
                    {formatAmount(product.price / monthsInPeriod)}
                  </span>
                  <span className="text-base text-system-white16 leading-[1.4]">/</span>
                  <span className="text-base text-system-white40 leading-[1.4]">{t('payments.per_monthly')}</span>
                  {product.discount > 0 && strikethroughPrice > 0 ? (
                    <span className="text-base text-system-white40 leading-[1.4] line-through">
                      {formatPrice(strikethroughPrice / monthsInPeriod)}
                    </span>
                  ) : null}
                </div>

                {product.billing_period === 'monthly' ? (
                  product.product_type === 'subscription' && product.discount && product.original_price ? (
                    <div className="mt-2 flex flex-col gap-2 text-sm text-system-white40">
                      <div>
                        {t('payments.plans.nextCycle', {
                          amount: formatPrice(product.original_price),
                        })}
                      </div>
                      <div className="flex flex-col gap-1">
                        {t.rich(
                          hasCoupon || showRetentionDiscount
                            ? 'payments.plans.subscriptionTerms'
                            : 'payments.plans.discountDesc',
                          { br: () => <br /> },
                        )}
                      </div>
                    </div>
                  ) : null
                ) : (
                  <div className="mt-2 text-sm sm:hidden">
                    {planType !== 'free' && (
                      <div className="text-system-white">
                        {t('payments.plans.billYearlyAs')}
                        {formatPrice(product.price)}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 订阅按钮 */}
        {renderSubscriptionButton && (
          <div className="mb-5" key={productSelector ? product.id : undefined}>
            {renderSubscriptionButton(product)}
          </div>
        )}

        {/* 积分权益 */}
        {planType !== 'free' && (
          <div className="relative mb-5 flex flex-col justify-center overflow-hidden rounded-16 bg-black/20 p-4">
            <ViduLogo2 className="-right-3 -bottom-3 absolute h-[70px] w-[85px] [&_g]:opacity-[0.05]" />
            <div className="relative z-10 flex flex-col gap-2">
              {productSelector}
              <div className="whitespace-nowrap font-medium text-sm text-system-white leading-5">
                {t.rich(useTrialCredits ? 'payments.plans.creditsProvideTrial' : 'payments.plans.creditsProvide', {
                  credits: () => (
                    <span className={`${CREDITS_CONFIG_LIST[planType].bgImg} mx-1 bg-clip-text text-transparent`}>
                      {displayCredits}
                    </span>
                  ),
                })}
              </div>

              <CreditsProgressBar
                planType={planType}
                progress={getCreditsProgress(planType, credits)}
                className="w-full flex-none"
              />

              {showLegacyNopeakChannel && (
                <div className="flex items-center gap-2 text-xs leading-5">
                  <span
                    className={`${CREDITS_CONFIG_LIST[planType].bgImg} size-[14px] shrink-0`}
                    style={{
                      maskImage: STAR_MASK,
                      WebkitMaskImage: STAR_MASK,
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat',
                    }}
                  />
                  <span className="bg-[linear-gradient(90deg,#D7DAFF_0%,#8E96FF_86.51%)] bg-clip-text text-transparent">
                    {t.rich('payments.plans.hightPriority', {
                      bold: (chunks) => <span className="font-semibold">{chunks}</span>,
                    })}
                  </span>
                  <CustomTooltip
                    content={t('payments.plans.staggerTipsApplicableModels')}
                    trigger="auto"
                    disableHoverableContent={true}
                    contentClassName="max-w-[260px] p-3"
                    container={document.body}
                  >
                    <BaseLineExclamation className="size-4 shrink-0 cursor-pointer text-[#9B93FF]" />
                  </CustomTooltip>
                </div>
              )}

              {extra_grant_credit > 0 && bonusCode && (
                <div className="flex items-center gap-1 text-sm text-system-white">
                  达人码专属
                  <span className={`${CREDITS_CONFIG_LIST[planType].bgImg} bg-clip-text text-transparent`}>
                    {extra_grant_credit}
                  </span>
                  积分
                </div>
              )}
            </div>
          </div>
        )}

        {/* 特殊权益模块：100积分单价 / 专享错峰通道 / Q4 模型消耗 */}
        {showBenefitHighlights && (
          <div className="mb-5 flex flex-col gap-2 rounded-16 border border-system-white12 bg-black/20 p-4">
            {(showCreditsUnitPrice || showNewNopeakChannel) && (
              <div className="flex items-center gap-2 self-start rounded-16 bg-system-white12 px-2.5 py-1 text-sm leading-5">
                <span
                  className="size-[14px] shrink-0"
                  style={{
                    backgroundImage: ULTIMATE_TITLE_GRADIENT,
                    maskImage: GIFT_MASK,
                    WebkitMaskImage: GIFT_MASK,
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                  }}
                />
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: ULTIMATE_TITLE_GRADIENT }}>
                  {t('payments.plans.limitedTimeActivity')}
                </span>
              </div>
            )}

            {showCreditsUnitPrice && (
              <div className="flex items-center gap-2 text-sm leading-5">
                <StarIcon className="size-[14px] shrink-0 text-[#9B93FF]" />
                <span className="flex-1 text-system-white">
                  {t('payments.plans.creditsUnitPrice', { price: formatPrice(creditsUnitPriceCents) })}
                </span>
              </div>
            )}

            {showNewNopeakChannel && (
              <div className="flex items-center gap-2 text-sm leading-5">
                <StarIcon className="size-[14px] shrink-0 text-[#9B93FF]" />
                <span className="text-system-white">
                  {t('payments.plans.nopeakDailyLimit', { count: NEW_MEMBERSHIP_NOPEAK_LIMITS[planType] })}
                </span>
                <CustomTooltip
                  content={t('payments.plans.staggerTipsApplicableModels')}
                  trigger="auto"
                  disableHoverableContent={true}
                  contentClassName="max-w-[260px] p-3"
                  container={document.body}
                >
                  <BaseLineExclamation className="size-4 shrink-0 cursor-pointer text-system-white40" />
                </CustomTooltip>
              </div>
            )}

            {showQ4Consumption && (
              <div className="flex items-center gap-2 text-sm leading-5">
                <StarIcon className="size-[14px] shrink-0 text-[#9B93FF]" />
                <span className="text-system-white">
                  {t('payments.plans.q4ModelConsumption', {
                    values: `540P ${Q4_CONSUMPTION_CONFIG[planType]['540P']}`,
                  })}
                </span>
                <CustomTooltip
                  content={
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{t('payments.plans.q4ConsumptionTipTitle')}</span>
                      {Q4_CONSUMPTION_RESOLUTIONS.map((resolution) => (
                        <span key={resolution}>
                          {t('payments.plans.q4ConsumptionTipLine', {
                            resolution,
                            credits: Q4_CONSUMPTION_CONFIG[planType][resolution],
                          })}
                        </span>
                      ))}
                    </div>
                  }
                  trigger="auto"
                  disableHoverableContent={true}
                  contentClassName="max-w-[260px] p-3"
                  container={document.body}
                >
                  <BaseLineExclamation className="size-4 shrink-0 cursor-pointer text-system-white40" />
                </CustomTooltip>
              </div>
            )}
          </div>
        )}

        {/* 高级权益 */}
        {highlightedRights.length > 0 && (
          <div className="mb-3 flex flex-col gap-3 text-left text-sm text-system-white">
            {highlightedRights.map((right) => {
              if (right === 'noWatermark' && SITE === 'china') return null;
              const isSpeedChannel = SPEED_CHANNEL_RIGHTS.has(right);
              return (
                <div key={right} className="flex items-center gap-2">
                  {isSpeedChannel ? (
                    <span
                      className={`${CREDITS_CONFIG_LIST[planType].bgImg} size-5 shrink-0`}
                      style={{
                        maskImage: SPARKLE_MASK,
                        WebkitMaskImage: SPARKLE_MASK,
                        maskSize: 'contain',
                        WebkitMaskSize: 'contain',
                        maskRepeat: 'no-repeat',
                        WebkitMaskRepeat: 'no-repeat',
                      }}
                    />
                  ) : (
                    <BaseFillIntelligentDuration className="size-5 shrink-0 text-[#9B93FF]" />
                  )}
                  <span
                    className={cn(
                      'flex flex-1 items-center font-normal',
                      isSpeedChannel && `${CREDITS_CONFIG_LIST[planType].bgImg} bg-clip-text text-transparent`,
                    )}
                  >
                    {(() => {
                      if (right === 'q2ImageUnlimited') {
                        return (
                          <>
                            {t.rich(`payments.plans.${right}`, { referenceNumber })}
                            <CustomTooltip
                              content={t(`payments.plans.${right}_tooltip`)}
                              trigger="auto"
                              disableHoverableContent={true}
                              container={document.body}
                            >
                              <BaseLineExclamation className="ml-1 size-4 shrink-0 cursor-pointer text-system-white40" />
                            </CustomTooltip>
                          </>
                        );
                      }
                      if (right === 'creditsPurchase') {
                        return t.rich('payments.plans.creditsPurchase', {
                          creditsDiscount: creditsDiscount,
                          deadline: (chunks) => <span className="text-system-white40">{chunks}</span>,
                        });
                      }
                      return t.rich(`payments.plans.${right}`, { referenceNumber });
                    })()}
                  </span>
                  {right === 'q2ImageUnlimited' && (
                    <span className="min-h-[17px] shrink-0 rounded-8 bg-[#5765F2] px-2 text-center font-normal text-white text-xs uppercase leading-[1.4]">
                      {t('payments.plans.unlimited365')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {highlightedRights.length > 0 && commonRights.length > 0 && (
          <div className="mt-2 mb-5 h-px bg-system-white08" />
        )}

        {/* 普通权益 */}
        <div className="flex flex-col gap-3 text-left text-sm text-system-white">
          {commonRights.map((right) => {
            if (right === 'noWatermark' && SITE === 'china') return null;
            return (
              <div key={right} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <BaseLineCheck2 className="size-4 shrink-0 text-[#888888]" />
                  <div className="flex items-center">
                    <span className="font-normal">
                      {(() => {
                        if (right === 'creditsPurchase') {
                          return t.rich('payments.plans.creditsPurchase', {
                            creditsDiscount: creditsDiscount,
                            deadline: (chunks) => <span className="text-system-white40">{chunks}</span>,
                          });
                        }
                        return t.rich(`payments.plans.${right}`, { referenceNumber });
                      })()}
                    </span>
                  </div>
                </div>
                {right === 'encodingFormatRight' && (
                  <div className="ml-6 flex items-center gap-1">
                    <BaseLineExclamation className="size-4 shrink-0 text-system-white40" />
                    <span className="text-sm text-system-white40 leading-5">
                      {t('payments.plans.encodingFormatRightTips')}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
